import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useCatalog } from '../context/CatalogContext';
import { AthleteProfileShell } from '../components/ProfileShell';
import Button from '../components/Button';
export default function MySportsPage() {
  const { user, API_URL } = useAuth();
  const catalog = useCatalog();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [profile, setProfile] = useState({
    sport: 'Football',
    position_primary: '',
    position_secondary: '',
    position_other: '',
    sports_played: [],
    jersey_number: '',
  });

  const [otherSports, setOtherSports] = useState(['']);
  const [visibleSportSlots, setVisibleSportSlots] = useState(1);

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        const d = response.data;
        setProfile(prev => ({ ...prev, ...d, sport: d.sport || 'Football', jersey_number: d.jersey_number ?? '' }));
        // Parse sports_played into slots (max 3)
        const sp = (d.sports_played || []).slice(0, 3);
        const count = Math.max(1, sp.length);
        const padded = Array.from({ length: count }, (_, i) => sp[i] || '');
        setOtherSports(padded);
        setVisibleSportSlots(count);
      }
    } catch (error) {
      console.log('No existing profile:', error);
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const saveField = async (fields) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, fields, getAxiosConfig());
      showMsg('Saved');
    } catch (error) {
      showMsg('Error saving', 'error');
    }
  };

  const handlePositionChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    saveField({ [field]: value });
  };

  // profile.sport stores the display name; map it to a catalog slug.
  const currentSportSlug =
    (catalog.sports.find(s => s.name === profile.sport) || {}).slug || 'football';
  const currentSport = catalog.getSport(currentSportSlug);
  const positionNoun = currentSport ? currentSport.position_noun : 'Position';

  const handleSportChange = (e) => {
    const newSlug = e.target.value;
    const newSport = catalog.sports.find(s => s.slug === newSlug);
    if (!newSport) return;
    const hadPositions = profile.position_primary || profile.position_secondary || profile.position_other;
    if (hadPositions && !window.confirm('Changing your sport will clear your selected positions. Continue?')) {
      return; // user cancelled; select reverts on next render
    }
    setProfile(prev => ({ ...prev, sport: newSport.name, position_primary: '', position_secondary: '', position_other: '' }));
    saveField({ sport: newSport.name, position_primary: '', position_secondary: '', position_other: '' });
  };

  const handleOtherSportChange = (index, value) => {
    const updated = [...otherSports];
    updated[index] = value;
    setOtherSports(updated);
  };

  const handleOtherSportBlur = () => {
    const filtered = otherSports.filter(s => s.trim());
    saveField({ sports_played: filtered });
  };

  const renderPositionSelect = (label, field) => (
    <div className="form-field">
      <label className="form-field__label" htmlFor={field}>{label}</label>
      <select
        id={field}
        className="form-field__select"
        value={profile[field] || ''}
        onChange={(e) => handlePositionChange(field, e.target.value)}
      >
        <option value="">Select {positionNoun.toLowerCase()}...</option>
        {catalog.groupedPositionsFor(currentSportSlug).map(group => (
          group.group ? (
            <optgroup key={group.group} label={group.group}>
              {group.positions.map(pos => (
                <option key={pos.slug} value={pos.label}>{pos.label}</option>
              ))}
            </optgroup>
          ) : (
            group.positions.map(pos => (
              <option key={pos.slug} value={pos.label}>{pos.label}</option>
            ))
          )
        ))}
      </select>
    </div>
  );

  return (
    <AthleteProfileShell activeTab="My Sports">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      {/* Sport + Positions */}
      <div className="settings-section">
        <h2 className="settings-section__title">My Sport</h2>
        <p className="settings-section__desc">Pick your sport — it sets your positions and measurables.</p>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="sport">Sport</label>
            <select
              id="sport"
              className="form-field__select"
              value={currentSportSlug}
              onChange={handleSportChange}
            >
              {catalog.sports.map(s => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <h2 className="settings-section__title" style={{ marginTop: 16 }}>{positionNoun}s</h2>
        <p className="settings-section__desc">Select up to three.</p>
        <div className="form-row">
          {renderPositionSelect(`Primary ${positionNoun}`, 'position_primary')}
          {renderPositionSelect(`Secondary ${positionNoun}`, 'position_secondary')}
        </div>
        <div className="form-row">
          {renderPositionSelect(`Other ${positionNoun}`, 'position_other')}
          <div className="form-field">
            <label className="form-field__label" htmlFor="jersey_number">Jersey Number</label>
            <input
              id="jersey_number"
              type="number"
              className="form-field__input"
              value={profile.jersey_number}
              onChange={(e) => setProfile(prev => ({ ...prev, jersey_number: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
              onBlur={() => {
                const raw = String(profile.jersey_number).trim();
                const val = raw === '' ? '' : parseInt(raw, 10);
                saveField({ jersey_number: val });
              }}
              placeholder="e.g. 12"
            />
          </div>
        </div>
      </div>

      {/* Other Sports */}
      <div className="settings-section">
        <h2 className="settings-section__title">Other Sports</h2>
        <p className="settings-section__desc">Select up to 3 additional sports you play.</p>
        {otherSports.slice(0, visibleSportSlots).map((sport, i) => {
          const selectedElsewhere = otherSports.filter((s, idx) => idx !== i && s);
          return (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
              <div className="form-field" style={{ flex: 1, maxWidth: '300px', marginBottom: 0 }}>
                <select
                  id={`sport_${i + 1}`}
                  className="form-field__select"
                  value={sport}
                  onChange={(e) => {
                    handleOtherSportChange(i, e.target.value);
                    const updated = [...otherSports];
                    updated[i] = e.target.value;
                    const filtered = updated.filter(s => s.trim());
                    saveField({ sports_played: filtered });
                  }}
                >
                  <option value="">Select sport...</option>
                  {catalog.sports.map(s => s.name)
                    .filter(name => name !== profile.sport && !selectedElsewhere.includes(name))
                    .map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                </select>
              </div>
              {i > 0 && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    const updated = otherSports.filter((_, idx) => idx !== i);
                    setOtherSports(updated);
                    setVisibleSportSlots(prev => prev - 1);
                    const filtered = updated.filter(s => s.trim());
                    saveField({ sports_played: filtered });
                  }}
                  style={{ flexShrink: 0 }}
                >
                  Remove
                </Button>
              )}
              {i === visibleSportSlots - 1 && visibleSportSlots < 3 && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setOtherSports(prev => [...prev, '']);
                    setVisibleSportSlots(prev => prev + 1);
                  }}
                  style={{ flexShrink: 0 }}
                >
                  + Add Sport
                </Button>
              )}
            </div>
          );
        })}
      </div>

    </AthleteProfileShell>
  );
}
